import React, { useEffect, useState, useCallback } from "react";
import styles from "./Dashboard.module.scss";
import moment from "moment";
import { db } from "../../firebase";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import Header from "../header/header";
import Form from "react-bootstrap/Form";

const ITEMS_PER_PAGE = 25;

const SECTION_OPTIONS = [
  { value: "all", label: "All sections" },
  { value: "Player Profile", label: "Player Profile" },
  { value: "Player Contact", label: "Player Contact" },
  { value: "Background Overview", label: "Background Overview" },
  { value: "Report", label: "Report" },
];

export default function MicroActivityLog() {
  const [logs, setLogs] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sectionFilter, setSectionFilter] = useState("all");
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(
    async (loadMore = false) => {
      if (loadMore) setLoadingMore(true);
      else setLoading(true);

      setError(null);

      try {
        let query = db.collection("micro-activity").orderBy("timestamp", "desc").limit(ITEMS_PER_PAGE);

        if (sectionFilter !== "all") {
          query = query.where("section", "==", sectionFilter);
        }

        if (loadMore && lastVisible) {
          query = query.startAfter(lastVisible);
        }

        const snapshot = await query.get();
        const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;

        setLogs((prev) => (loadMore ? [...prev, ...items] : items));
        setLastVisible(lastDoc);
      } catch (fetchError) {
        console.error("Error fetching micro activity logs:", fetchError);
        setError("Unable to load change log. If filtering by section, a Firestore index may be required.");
        if (!loadMore) setLogs([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [sectionFilter, lastVisible]
  );

  useEffect(() => {
    setLastVisible(null);
    fetchLogs(false);
  }, [sectionFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    const formatted = moment.unix(timestamp);
    return formatted.isValid() ? formatted : null;
  };

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.feed}>
        <div className={styles.topBar}>
          <p>Change Log</p>
          <Form.Select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            style={{ minWidth: 200, maxWidth: 260 }}
            aria-label="Filter by section"
          >
            {SECTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>

        {error && (
          <p style={{ color: "#b42318", padding: "8px 0 0" }}>{error}</p>
        )}

        <div className={styles.tableContainer}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Player</th>
                <th>Section</th>
                <th>Action</th>
                <th>Detail</th>
                <th>Editor</th>
              </tr>
            </thead>
            {loading ? (
              <tbody>
                {[...Array(5)].map((_, index) => (
                  <tr key={index} className={styles["skeleton-row"]}>
                    <td colSpan="7">
                      <div className={styles["skeleton-cell"]}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "24px" }}>
                      No change log entries yet.
                    </td>
                  </tr>
                ) : (
                  logs.map((item) => {
                    const timestamp = formatTimestamp(item.timestamp);
                    return (
                      <tr key={item.id}>
                        <td>{timestamp ? timestamp.format("l") : "—"}</td>
                        <td>{timestamp ? timestamp.format("hh:mm A") : "—"}</td>
                        <td>
                          {item.playerID ? (
                            <Link to={`/player-profile/detail/${item.playerID}`}>
                              {item.playerName || "Player"}
                            </Link>
                          ) : (
                            item.playerName || "—"
                          )}
                        </td>
                        <td>{item.section || "—"}</td>
                        <td style={{ textTransform: "capitalize" }}>{item.action || "—"}</td>
                        <td>{item.detail || "—"}</td>
                        <td>{item.editor || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            )}
          </table>

          <div className={styles.centeredButton}>
            {loadingMore ? (
              <p>Loading more...</p>
            ) : (
              <button
                type="button"
                onClick={() => fetchLogs(true)}
                disabled={loading || loadingMore || !lastVisible}
              >
                Load More
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
